import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DbEvent } from '../types';
import { supabase } from '../lib/supabase';
import { calcTotal, calcBalance, isOverdue, fmtMoney, fmtDate } from '../lib/pricing';

type Filter = 'all' | 'unpaid' | 'partial' | 'paid' | 'overdue';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unpaid', label: 'Unpaid' },
  { key: 'partial', label: 'Partial' },
  { key: 'paid', label: 'Paid' },
  { key: 'overdue', label: 'Overdue' },
];

export function Payments() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<DbEvent[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('events')
      .select('*, clients(*)')
      .neq('status', 'cancelled')
      .order('event_date', { ascending: false })
      .then(({ data }) => { setEvents(data ?? []); setLoading(false); });
  }, []);

  const filtered = events.filter(e => {
    const paid = e.amount_paid ?? 0;
    const balance = calcBalance(e);
    const overdue = isOverdue(e);
    if (filter === 'all') return true;
    if (filter === 'unpaid') return paid === 0 && balance > 0;
    if (filter === 'partial') return paid > 0 && balance > 0 && !overdue;
    if (filter === 'paid') return balance <= 0;
    if (filter === 'overdue') return overdue;
    return true;
  });

  const totalOutstanding = events.reduce((s, e) => s + Math.max(0, calcBalance(e)), 0);

  const rowStatus = (e: DbEvent): { label: string; color: string } => {
    const balance = calcBalance(e);
    const paid = e.amount_paid ?? 0;
    if (isOverdue(e)) return { label: 'Overdue', color: 'text-red-600 bg-red-50' };
    if (balance <= 0) return { label: 'Paid', color: 'text-green-600 bg-green-50' };
    if (paid > 0) return { label: 'Partial', color: 'text-yellow-700 bg-yellow-50' };
    return { label: 'Unpaid', color: 'text-gray-600 bg-gray-100' };
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <h1 className="font-serif text-2xl font-bold text-gray-900 mb-2">Payments</h1>

      {/* Outstanding summary */}
      <div className="bg-gold-50 border border-gold-200 rounded-xl px-4 py-3 mb-5 flex items-center justify-between">
        <span className="text-sm text-gold-800 font-medium">Total Outstanding</span>
        <span className="text-xl font-bold text-gold-700">{fmtMoney(totalOutstanding)}</span>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 overflow-x-auto no-scrollbar mb-4">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
              filter === f.key ? 'bg-gold-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">No events match this filter</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Table header — desktop */}
          <div className="hidden md:grid grid-cols-7 px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            <span className="col-span-2">Client</span>
            <span>Date</span>
            <span className="text-right">Total</span>
            <span className="text-right">Paid</span>
            <span className="text-right">Balance</span>
            <span className="text-right">Status</span>
          </div>

          <div className="divide-y divide-gray-100">
            {filtered.map(e => {
              const total = calcTotal(e);
              const paid = e.amount_paid ?? 0;
              const balance = calcBalance(e);
              const overdue = isOverdue(e);
              const { label, color } = rowStatus(e);

              return (
                <button
                  key={e.id}
                  onClick={() => navigate(`/events/${e.id}`)}
                  className={`w-full text-left transition-colors ${overdue ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}
                >
                  {/* Mobile layout */}
                  <div className="md:hidden px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{e.clients?.name ?? '—'}</p>
                        <p className="text-xs text-gray-500">{e.event_type} · {fmtDate(e.event_date)}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${color}`}>{label}</span>
                    </div>
                    <div className="flex gap-4 mt-1.5 text-xs">
                      <span className="text-gray-500">Total: <span className="text-gray-800 font-medium">{fmtMoney(total)}</span></span>
                      <span className="text-gray-500">Paid: <span className="text-green-700 font-medium">{fmtMoney(paid)}</span></span>
                      <span className="text-gray-500">Bal: <span className={`font-medium ${overdue ? 'text-red-600' : 'text-gray-800'}`}>{fmtMoney(balance)}</span></span>
                    </div>
                    {e.payment_due_date && (
                      <p className="text-xs text-gray-400 mt-0.5">Due: {fmtDate(e.payment_due_date)}</p>
                    )}
                  </div>

                  {/* Desktop layout */}
                  <div className="hidden md:grid grid-cols-7 px-4 py-3 items-center text-sm">
                    <span className="col-span-2 font-medium text-gray-900 truncate">{e.clients?.name ?? '—'}</span>
                    <span className="text-gray-500">{fmtDate(e.event_date)}</span>
                    <span className="text-right">{fmtMoney(total)}</span>
                    <span className="text-right text-green-600">{fmtMoney(paid)}</span>
                    <span className={`text-right font-semibold ${overdue ? 'text-red-600' : balance > 0 ? 'text-gray-900' : 'text-green-600'}`}>{fmtMoney(balance)}</span>
                    <div className="flex justify-end">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${color}`}>{label}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
